import { Alert, Button, Input, Space, Spin, Typography } from 'antd';
import React, { useRef } from 'react';

import { NoteCardEditorShell } from '@/components/common/NoteCardEditorShell';
import { SkillTagManager } from '@/components/common/SkillTagManager';
import {
  SkillCrepeBodyEditor,
  type SkillCrepeBodyEditorHandle,
} from '@/components/editor/SkillCrepeBodyEditor';
import { useSkillEditor, type SkillEditorMode } from '@/hooks/useSkillEditor';

const { Text } = Typography;

type SaveState = 'saved' | 'saving' | 'unsaved';

export interface SkillEditorPageProps {
  accessToken: string | null | undefined;
  mode: SkillEditorMode;
  templateKey?: string;
  versionId?: string;
}

const getSkillSaveStateLabel = (saveState: SaveState): string => {
  const labels: Record<SaveState, string> = {
    saved: 'Saved',
    saving: 'Saving',
    unsaved: 'Unsaved',
  };
  return labels[saveState];
};

const getSkillPrimaryActionLabel = (mode: SkillEditorMode): string => {
  const labels: Record<SkillEditorMode, string> = {
    new: 'Save skill',
    template: 'Fork to library',
    version: 'Save new version',
  };
  return labels[mode];
};

function canRunSkillPrimaryAction(
  mode: SkillEditorMode,
  templateKey: string | undefined,
  versionId: string | undefined,
  dirty: boolean,
  saveState: SaveState,
  title: string,
  body: string,
): boolean {
  if (!dirty || saveState === 'saving' || !title.trim() || !body) {
    return false;
  }
  if (mode === 'template') {
    return Boolean(templateKey);
  }
  if (mode === 'version') {
    return Boolean(versionId && versionId !== 'new');
  }
  return mode === 'new';
}

export const SkillEditorPage: React.FC<SkillEditorPageProps> = ({
  accessToken,
  mode,
  templateKey,
  versionId,
}) => {
  const {
    loading,
    error,
    title,
    description,
    tags,
    body,
    savedTitle,
    savedDescription,
    savedTags,
    savedBody,
    saveState,
    templateBundleMeta,
    setTitle,
    setDescription,
    setTags,
    setBody,
    setUnsavedIfNeeded,
    handleSave,
  } = useSkillEditor(accessToken, mode, templateKey, versionId);
  const bodyEditorRef = useRef<SkillCrepeBodyEditorHandle | null>(null);

  const instanceKey = `${mode}-${templateKey ?? ''}-${versionId ?? 'new'}`;

  const liveBody = (bodyEditorRef.current?.getMarkdown() ?? body).trim();
  const tagsEqual = tags.length === savedTags.length && tags.every((t, i) => t === savedTags[i]);
  const dirty =
    title.trim() !== savedTitle.trim() ||
    description.trim() !== savedDescription.trim() ||
    !tagsEqual ||
    liveBody !== savedBody.trim();

  const statusLabel = getSkillSaveStateLabel(saveState);

  const canPrimaryAction = canRunSkillPrimaryAction(
    mode,
    templateKey,
    versionId,
    dirty,
    saveState,
    title,
    liveBody,
  );

  const primaryActionLabel = getSkillPrimaryActionLabel(mode);

  const handleSaveWithLiveMarkdown = (): Promise<void> =>
    handleSave((bodyEditorRef.current?.getMarkdown() ?? body).trim());

  const handleEditorShortcut = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      if (canPrimaryAction) {
        void handleSaveWithLiveMarkdown();
      }
    }
  };

  if (!accessToken) {
    return <Alert type="warning" showIcon message="Sign in to edit skills." />;
  }

  return (
    <div className="page-stack create-note-page skill-editor-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {error ? <Alert type="error" showIcon message={error} /> : null}

        {mode === 'template' && templateKey && !loading && !error ? (
          <Alert
            type="info"
            showIcon
            message="Bundled templates are not stored in the database."
            description={
              <>
                Author, bundle version, description, and tags come from the template{' '}
                <code>.meta.yaml</code> sidecar (when present) and are prefilled below—you can edit
                them before using Fork to library. The bundled markdown file is never updated in
                place. After you fork, use Save new version when editing your saved copy.
              </>
            }
          />
        ) : null}

        {loading ? (
          <Spin />
        ) : (
          <NoteCardEditorShell
            titleRow={
              <>
                <div className="create-note-page__title-row">
                  <input
                    aria-label="Skill title"
                    className="create-note-title-input"
                    placeholder="Enter title…"
                    value={title}
                    onChange={(event) => {
                      setTitle(event.target.value);
                      setUnsavedIfNeeded();
                    }}
                  />
                  <Text
                    data-testid="skill-save-state"
                    className="create-note-save-indicator"
                    type={saveState === 'saved' ? 'success' : 'warning'}
                  >
                    {statusLabel}
                  </Text>
                </div>
                {mode === 'template' &&
                templateKey &&
                (templateBundleMeta?.author || templateBundleMeta?.metaVersion) ? (
                  <Text type="secondary" className="skill-editor-page__template-byline">
                    {[
                      templateBundleMeta?.author ? `Author: ${templateBundleMeta.author}` : null,
                      templateBundleMeta?.metaVersion
                        ? `Bundle v${templateBundleMeta.metaVersion}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                ) : null}
              </>
            }
            tagRow={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  marginTop: 4,
                }}
              >
                <SkillTagManager
                  tags={tags}
                  onTagsChange={(next) => {
                    setTags(next);
                    setUnsavedIfNeeded();
                  }}
                />
              </div>
            }
            betweenTagsAndTip={
              <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                  Description
                </Text>
                <Input.TextArea
                  aria-label="Skill description"
                  placeholder="Short summary shown in the Skills library…"
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setUnsavedIfNeeded();
                  }}
                />
              </div>
            }
            tipRow={
              <div className="create-note-page__tip-row">
                <Text type="secondary" className="create-note-page__tip">
                  Milkdown tip: use slash commands in the editor with <code>/</code>.
                </Text>
              </div>
            }
            editorRegion={
              <div
                className="create-note-page__editor-region"
                style={{ marginTop: 4 }}
                onKeyDownCapture={handleEditorShortcut}
              >
                <SkillCrepeBodyEditor
                  ref={bodyEditorRef}
                  key={instanceKey}
                  instanceKey={instanceKey}
                  initialMarkdown={body}
                  onMarkdownChange={(md) => {
                    setBody(md);
                    setUnsavedIfNeeded();
                  }}
                />
              </div>
            }
            footer={
              <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                <Button
                  type="primary"
                  onClick={() => void handleSaveWithLiveMarkdown()}
                  disabled={!canPrimaryAction}
                >
                  {primaryActionLabel}
                </Button>
              </Space>
            }
          />
        )}
      </Space>
    </div>
  );
};
